import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import { PageHeader, Section } from '../../components/ui/index.jsx';

const KINDS = [
  ['file', 'Upload an email'],
  ['paste', 'Paste an email'],
  ['approach', 'A call or message'],
];
const CHANNELS = [
  ['phone', 'Phone call'], ['sms', 'Text message'], ['whatsapp', 'WhatsApp'], ['social', 'Social media message'], ['in_person', 'In person'], ['other', 'Another way'],
];
const MAX_FILE = 15 * 1024 * 1024;

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('That file couldn’t be read.'));
  r.readAsDataURL(file);
});

/** A drop zone that is also a file input (keyboard and screen readers use the input). */
function FileDrop({ file, onFile, error }) {
  const id = useId();
  const [over, setOver] = useState(false);
  return (
    <div className="field">
      <label htmlFor={id}>Email file</label>
      <div className={`file-drop${over ? ' is-over' : ''}${error ? ' is-invalid' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]); }}>
        <input id={id} type="file" accept=".eml,.msg,.mbox,.txt,message/rfc822,application/vnd.ms-outlook" onChange={(e) => onFile(e.target.files?.[0] || null)} />
        <p className="file-drop-text">{file ? <><strong>{file.name}</strong> <span className="muted">· {Math.ceil(file.size / 1024)} KB</span></> : <>Drag the email here, or <span className="link-like">choose a file</span></>}</p>
      </div>
      <p className="field-hint">.eml (Gmail, Apple Mail, Thunderbird, Outlook on the web), .msg (Outlook) or .mbox, up to 15 MB. In Outlook you can drag the email straight from your inbox onto this box.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}

/** Add a scam or phishing attempt yourself: an email, or a call or message. */
export default function InboxAdd() {
  const { organisation } = useAuth();
  const navigate = useNavigate();
  const [kind, setKind] = useState('file');
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [a, setA] = useState({ channel: 'phone', numbers: '', when: '', caller_name: '', claimed_org: '', asked: '', details: '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => { setA({ ...a, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  useEffect(() => {
    if (!organisation) return;
    api('/portal/events').then((d) => setEvents((d.events || []).filter((e) => e.status !== 'archived' && (!e.organisation_id || Number(e.organisation_id) === Number(organisation.id))))).catch(() => setEvents([]));
  }, [organisation]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setErrors({});
    let body;
    if (kind === 'file') {
      if (!file) { setErrors({ file: 'Choose the email file.' }); return; }
      if (file.size > MAX_FILE) { setErrors({ file: 'That file is too big (15 MB at most).' }); return; }
      body = { type: 'file', filename: file.name, content_base64: await toBase64(file) };
    } else if (kind === 'paste') {
      if (text.trim().length < 20) { setErrors({ text: 'Paste the whole email: its text, or better, its source.' }); return; }
      body = { type: 'paste', text };
    } else {
      if (!a.numbers.trim() && a.details.trim().length < 10) { setErrors({ details: 'Give the number they used, or describe what happened.' }); return; }
      body = { type: 'approach', approach: a };
    }
    if (eventId) body.event_id = Number(eventId);
    setBusy(true);
    try {
      const res = await api(`/portal/organisations/${organisation.id}/inbox/add`, { method: 'POST', body });
      navigate(`/inbox/${res.id}`, { state: { notice: res.existing ? 'You’d already added this email — here it is.' : 'Added. We’re checking it now: it takes a few seconds.' } });
    } catch (err) {
      setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader back={{ to: '/inbox', label: 'Messages' }} title="Add a scam"
        subtitle="A scam or phishing email someone sent you or a colleague, or a call or message pretending to be you. We check it like any email forwarded to us, then you decide what it is — and once you confirm it, it’s caught at every event we protect." />
      <form onSubmit={submit} noValidate>
        <div className="segmented add-kinds" role="tablist" aria-label="What are you adding?">
          {KINDS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? 'is-active' : ''} onClick={() => { setKind(k); setErrors({}); setError(''); }}>{label}</button>
          ))}
        </div>
        <Section>
          {error && <Alert tone="danger">{error}</Alert>}
          {kind === 'file' && <FileDrop file={file} onFile={(f) => { setFile(f); setErrors({}); }} error={errors.file} />}
          {kind === 'paste' && (
            <>
              <Field as="textarea" label="The email" rows={14} value={text} onChange={(e) => { setText(e.target.value); setErrors({}); }} error={errors.text}
                hint={!errors.text ? 'Best: its source, with the headers — Gmail: ⋮ → Show original; Outlook: … → View → View message source; Apple Mail: View → Message → Raw Source. Otherwise paste its text, with the From, Date and Subject lines if you can.' : undefined} />
            </>
          )}
          {kind === 'approach' && (
            <>
              <Field as="select" label="How did they get in touch?" value={a.channel} onChange={(e) => setField('channel', e.target.value)} error={errors.channel}>
                {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Field>
              <Field label="The number they used" optional value={a.numbers} onChange={(e) => setField('numbers', e.target.value)} error={errors.numbers}
                placeholder="+44 20 7946 0000" inputMode="tel" hint={!errors.numbers ? 'Separate several with commas. Once you confirm it’s a scam, anyone checking this number on your verify page is warned.' : undefined} />
              <div className="grid-2">
                <Field label="Name they gave" optional maxLength={120} value={a.caller_name} onChange={(e) => setField('caller_name', e.target.value)} />
                <Field label="Who they said they were from" optional maxLength={160} value={a.claimed_org} onChange={(e) => setField('claimed_org', e.target.value)} placeholder={organisation?.name} />
              </div>
              <div className="grid-2">
                <Field label="When" optional type="date" value={a.when} onChange={(e) => setField('when', e.target.value)} />
                <Field label="What they asked for" optional maxLength={500} value={a.asked} onChange={(e) => setField('asked', e.target.value)} placeholder="Card details for a hotel booking" />
              </div>
              <Field as="textarea" label="What happened" rows={6} maxLength={5000} counter value={a.details} onChange={(e) => setField('details', e.target.value)} error={errors.details}
                hint={!errors.details ? 'In your own words: what they said, who they contacted, anything they sent afterwards.' : undefined} />
            </>
          )}
          {events.length > 0 && (
            <Field as="select" label="Which event is it about?" optional value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Not one event in particular</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </Field>
          )}
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add and check it'}</button>
          </div>
        </Section>
      </form>
    </div>
  );
}
